import React from 'react';
import { colors } from '../../../tailwind/colors';

export type AvatarColor = 'coral' | 'mint' | 'purple' | 'sky' | 'rose' | 'yellow' | 'sage' | 'peach' | 'teal';

type AvatarColorEntry = { from: string; to: string };
const AVATAR_COLOR_PALETTE = Object.fromEntries(
  Object.entries(colors.avatarColors as Record<string, AvatarColorEntry>).map(
    ([name, { from, to }]) => [name, `linear-gradient(135deg, ${from}, ${to})`],
  ),
) as Record<AvatarColor, string>;

export interface AvatarProps {
  /** User initials or text to display */
  children: React.ReactNode;
  /** Avatar size */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Avatar variant */
  variant?: 'user' | 'customer';
  /** Avatar color for user variant (default: 'mint') */
  avatarColor?: AvatarColor;
  /** Custom gradient for user variant (overrides avatarColor) */
  customGradient?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Avatar component from AgroBook design system
 *
 * User avatars have gradient background (uses avatarColor palette or default green)
 * Customer avatars have solid green-200 background
 */
export const Avatar: React.FC<AvatarProps> = ({
  children,
  size = 'md',
  variant = 'user',
  avatarColor = 'mint',
  customGradient,
  className = '',
}) => {
  const sizeStyles = {
    sm: 'w-10 h-10 text-2xs',
    md: 'w-12 h-12 text-md',
    lg: 'w-16 h-16 text-lg',
    xl: 'w-20 h-20 text-xl font-display',
  };

  const getVariantStyle = () => {
    if (variant === 'customer') {
      return 'bg-green-200 text-green-900';
    }
    // For user variant, use the color palette
    return 'text-white';
  };

  const baseStyle = `
    rounded-full flex items-center justify-center font-bold
    ${sizeStyles[size]}
    ${getVariantStyle()}
    ${className}
  `;

  if (variant === 'user') {
    return (
      <div
        className={baseStyle}
        style={{ backgroundImage: customGradient || AVATAR_COLOR_PALETTE[avatarColor] }}
      >
        {children}
      </div>
    );
  }

  return (
    <div className={baseStyle}>
      {children}
    </div>
  );
};
